import React from "react";
import Axios from "axios";
import { PublicClientApplication, AuthenticationResult } from "@azure/msal-browser";
import { authConfig, loginRequest, silentRequest } from "./authConfig";
import LoginScreen from "../LoginScreen/LoginScreen";
import { Modal, Image } from "semantic-ui-react";
import styles from "./AuthProvider.module.scss";
import koneLogo from "../../images/kone-logo-blue.svg";

const publicClientApplication = new PublicClientApplication(authConfig);

export const logout = (): void => {
  publicClientApplication.logout();
  localStorage.clear();
};

export interface LoginState {
  loginStatus?: loginStatusOptions;
  errorMsg?: string;
  expFlag: boolean;
}

export type loginStatusOptions =
  | "Redirecting to login"
  | "Logged In"
  | "Authenticating user"
  | "Login Error"
  | "Bypass"
  | "Prompt";

// eslint-disable-next-line
export function AuthProvider(WrappedComponent: any): any {
  // eslint-disable-next-line
  return class extends React.Component<LoginState> {
    state: LoginState = { loginStatus: "Prompt", expFlag: false };

    /**
     * Initiates the login flow for the user
     */
    login = async (): Promise<void> => {
      try {
        await publicClientApplication.loginRedirect(loginRequest);
      } catch (e) {
        console.error("Login error: " + e);
        this.setState({
          loginStatus: "Login Error",
          errorMsg: "Failed to login"
        });
      }
    };

    /**
     * Handles the result of the login response
     * @param {AuthenticationResult | null} response A response object from the AAD login
     */
    handleAuthenticationResult = (response: AuthenticationResult | null): void => {
      if (response?.accessToken) {
        // Add access token to every axios request header
        Axios.defaults.headers.common.Authorization = "Bearer " + response.accessToken;
        Axios.defaults.headers.xsrfHeaderName = "X-XSRF-TOKEN";
        Axios.defaults.headers.xsrfCookieName = "XSRF-TOKEN";
        Axios.defaults.withCredentials = true;
        this.setState({ loginStatus: "Logged In" });
        this.showSessionError();
      }
    };

    /**
     * Tries to login user with active session silenty.
     * User is prompted to re-enter the credentials if the session is expired or expires soon
     */
    trySilentLogin = async (): Promise<void> => {
      const accounts = publicClientApplication.getAllAccounts();

      // Currently supports silent login for only one session at a time
      if (accounts?.length === 1) {
        const username = accounts[0].username;
        try {
          // See: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/login-user.md#silent-login-with-ssosilent
          const loginResponse: AuthenticationResult = await publicClientApplication.ssoSilent(
            silentRequest(username)
          );
          this.handleAuthenticationResult(loginResponse);
        } catch (error) {
          // We seem to end up here, when we are falsely trying to use MSAL account that has been changed in another browser window.
          // The error parameter in catch seems always to be undefined (or invalid), as it can not be used even for comparing!
          // So, no error handling here, just provide user the error page with logout button, for avoiding login redirect loop.
          this.setState({
            loginStatus: "Login Error",
            errorMsg: "Something went wrong during the login. Please try again later"
          });
        }
      }
    };

    showSessionError() {
      if (localStorage.getItem("token") != null) {
        const token: string | null = localStorage.getItem("token");
        if (token) {
          const diff = new Date(JSON.parse(token).expiresOn).getTime() - new Date().getTime();
          if (diff > 0) {
            setTimeout(() => {
              window.addEventListener("popstate", this.handleBeforeUnload);
              this.setState({
                expFlag: true
              });
            }, diff);
          } else {
            localStorage.clear();
            this.setState({ loginStatus: "Prompt", expFlag: false });
          }
        }
      }
    }

    handleBeforeUnload = () => {
      this.setState({ loginStatus: "Prompt", expFlag: false });
    };

    async componentDidMount(): Promise<void> {
      Axios.interceptors.response.use(
        response => response,
        error => {
          console.log("Error intercepted:", error.response.status);
          if (error.response.status === 401) {
            localStorage.removeItem("token");
            this.setState({ loginStatus: "Prompt", expFlag: false });
          }
          return Promise.reject(error);
        }
      );
      // If aad config is not set, bypass AAD login. Pdapi does the real authentication, so this does not allow access to any data
      // However, this can be removed when aad auth is working
      if ((process.env.REACT_APP_APPID || "") === "") {
        this.setState({ loginStatus: "Bypass" });
      } else {
        // First try to silently login the user

        // Handler for the loginRedirect. Not my favorite way of doing things but
        // it is required because of React component lifecycles.
        if (localStorage.getItem("token") != null) {
          const token: string | null = localStorage.getItem("token");
          if (token) {
            this.handleAuthenticationResult(JSON.parse(token));
          }
        } else {
          publicClientApplication
            .handleRedirectPromise()
            .then(async (tokenResponse: null | AuthenticationResult) => {
              if (tokenResponse != null) {
                localStorage.setItem("token", JSON.stringify(tokenResponse));
              }
              this.handleAuthenticationResult(tokenResponse);
            })
            .catch(error => {
              console.error("error: " + error);
            });
        }
      }
    }

    componentWillUnmount() {
      window.removeEventListener("popstate", this.handleBeforeUnload);
    }

    render(): JSX.Element {
      if (this.state.loginStatus === "Logged In" || this.state.loginStatus === "Bypass") {
        const getNewToken = () => {
          localStorage.removeItem("token");
          const request = {
            scopes: [(process.env.REACT_APP_SCOPES || "").split(",")[0]],
            account: publicClientApplication.getAllAccounts()[0]
          };
          publicClientApplication.acquireTokenSilent(request).then(async response => {
            localStorage.setItem("token", JSON.stringify(response));
            this.handleAuthenticationResult(response);
            this.setState({
              expFlag: false
            });
          });
        };
        return (
          <>
            <WrappedComponent
              {...this.props}
              bypassAuthorization={this.state.loginStatus === "Bypass"}
            />
            <Modal basic open={this.state.expFlag} size="large">
              <Modal.Content className={styles.content}>
                <div className={styles.Expirybox}>
                  <div className={styles.insidecontainter}>
                    <div>
                      <Image
                        className={styles.koneLogo}
                        src={koneLogo}
                        id="koneLogo"
                        alt="koneLogo"
                      />
                    </div>
                    <div>
                      <p className={styles.bgcolor}>Kce DataBase Tools</p>
                    </div>
                    <div className={styles.boxSpace}>
                      <p className={styles.errTxt}>Session Expired</p>
                      <button onClick={() => getNewToken()} className={styles.buttonColor}>
                        Continue as {publicClientApplication.getAllAccounts()[0].name}
                      </button>
                    </div>
                  </div>
                </div>
              </Modal.Content>
            </Modal>
          </>
        );
      } else if (this.state.loginStatus === "Login Error") {
        return (
          <div>
            <p>Login error. Please try again.</p>
            <p>{this.state.errorMsg}</p>
            <button onClick={() => logout()}>Log out</button>
          </div>
        );
      } else {
        return (
          <LoginScreen
            loginStatus={this.state.loginStatus || "Prompt"}
            onLoginClick={() => this.login()}
          />
        );
      }
    }
  };
}
